import { recordedIso, recordedBooleanLabel } from '@/utils/recordedValues'
/**
 * Audit Log Page
 * Displays audit trail for security and compliance
 * Accessible to Owner and Admin roles
 */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  FileText,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useAuditStore } from '@/store/auditStore'
import { toast } from '@/store/toastStore'
import { AuditLogTable, AuditLogFilters } from '@/components/audit'
import type { AuditLogFilters as AuditFilters, AuditResourceType } from '@/types/session.types'

// ============================================================================
// STATS CARD COMPONENT
// ============================================================================

function StatsCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: number
  icon: React.ElementType
  color: 'blue' | 'green' | 'red' | 'orange'
}) {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  }

  return (
    <div className="card card-elevated p-4 hover:shadow-lg transition">
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-text-secondary text-sm font-medium">{label}</p>
          <p className="text-text-primary text-2xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user: currentUser, hasPermission } = useAuth()
  const canExport = hasPermission('txh.audit.export')

  // Store
  const { entries, isLoading, loadAuditLog, setFilters: setStoreFilters } = useAuditStore()

  // Filters - initialize from URL params
  const [filters, setFilters] = useState<AuditFilters>({
    action: (searchParams.get('action') as AuditFilters['action']) || 'all',
    resourceType: (searchParams.get('resourceType') as AuditResourceType) || undefined,
    userId: searchParams.get('userId') || undefined,
    success:
      searchParams.get('success') === 'true'
        ? true
        : searchParams.get('success') === 'false'
          ? false
          : 'all',
    dateFrom: searchParams.get('dateFrom') || undefined,
    dateTo: searchParams.get('dateTo') || undefined,
    searchQuery: searchParams.get('search') || undefined,
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const pageSize = 20

  // Load entries on mount and filter change
  useEffect(() => {
    if (currentUser?.tenantId) {
      setStoreFilters(filters)
      loadAuditLog()
    }
  }, [currentUser?.tenantId, filters, loadAuditLog, setStoreFilters])

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (filters.action && filters.action !== 'all') params.set('action', filters.action)
    if (filters.resourceType) params.set('resourceType', filters.resourceType)
    if (filters.userId) params.set('userId', filters.userId)
    if (filters.success !== 'all') params.set('success', String(filters.success))
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
    if (filters.dateTo) params.set('dateTo', filters.dateTo)
    if (filters.searchQuery) params.set('search', filters.searchQuery)
    setSearchParams(params, { replace: true })
  }, [filters, setSearchParams])

  // Handle filter changes
  const handleFiltersChange = (newFilters: Partial<AuditFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters({
      action: 'all',
      success: 'all',
    })
    setCurrentPage(1)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await loadAuditLog()
    setIsRefreshing(false)
  }

  const handleExport = () => {
    if (!canExport) return
    // In a real app, this would export to CSV
    const csv = [
      ['Timestamp', 'Action', 'User', 'Resource', 'Status', 'IP Address'],
      ...entries.map(e => [
        recordedIso(e.createdAt),
        e.action,
        e.userEmail || 'System',
        e.resourceType || '-',
        recordedBooleanLabel(e.success, 'Success', 'Failed'),
        e.ipAddress || '-',
      ]),
    ]
      .map(row => row.join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Audit log exported', `${entries.length} entries downloaded as CSV.`)
  }

  // Filter entries locally (in addition to API filters)
  const filteredEntries = entries.filter(entry => {
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase()
      const matchesSearch =
        entry.userEmail?.toLowerCase().includes(query) ||
        entry.performedByName?.toLowerCase().includes(query) ||
        entry.action.toLowerCase().includes(query) ||
        JSON.stringify(entry.details ?? null)
          .toLowerCase()
          .includes(query)
      if (!matchesSearch) return false
    }
    return true
  })

  // Paginate
  const totalPages = Math.ceil(filteredEntries.length / pageSize)
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  // Calculate stats
  const stats = {
    total: entries.length,
    successful: entries.filter(e => e.success === true).length,
    failed: entries.filter(e => e.success === false).length,
    unrecorded: entries.filter(e => e.success == null).length,
    logins: entries.filter(e => e.action === 'login').length,
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Audit Log</h1>
          <p className="text-text-secondary mt-1">
            Track all security and administrative actions in your organization
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="btn btn-outline gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard label="Total Events" value={stats.total} icon={FileText} color="blue" />
        <StatsCard label="Successful" value={stats.successful} icon={CheckCircle} color="green" />
        <StatsCard label="Failed" value={stats.failed} icon={XCircle} color="red" />
        <StatsCard label="Login Events" value={stats.logins} icon={Shield} color="orange" />
      </div>

      {stats.unrecorded > 0 && (
        <p className="text-sm text-text-tertiary">
          Results are not recorded for {stats.unrecorded} loaded entries.
        </p>
      )}

      {/* Compliance Notice */}
      <div className="card card-elevated p-4 border-l-4 border-primary-500 bg-primary-50/50 dark:bg-primary-900/10">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-text-primary">Compliance & Security</h3>
            <p className="text-sm text-text-secondary mt-1">
              Audit records support security and compliance review.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card card-elevated p-4">
        <AuditLogFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          onExport={canExport ? handleExport : undefined}
          totalCount={entries.length}
          filteredCount={filteredEntries.length}
        />
      </div>

      {/* Audit Table */}
      <AuditLogTable entries={paginatedEntries} isLoading={isLoading} showUser={true} />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, filteredEntries.length)} of {filteredEntries.length}{' '}
            entries
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn btn-ghost btn-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`btn btn-sm ${
                      currentPage === pageNum ? 'btn-primary' : 'btn-ghost'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-ghost btn-sm"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Security Warning */}
      {stats.failed > 0 && (
        <div className="card card-elevated p-4 border-l-4 border-orange-500 bg-orange-50/50 dark:bg-orange-900/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-text-primary">Security Alert</h3>
              <p className="text-sm text-text-secondary mt-1">
                There are {stats.failed} failed event{stats.failed !== 1 ? 's' : ''} in the current
                view. Review failed login attempts and other security events regularly.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
